import { AnalyticsMembershipEvent, LinkState, Provider } from '@fruitful/types';
import { useAtomValue } from 'jotai';
import { groupBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    Icon,
    InfoIcon,
    PlusIcon,
    ScreenContainer,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { AddInstitutionSheet } from '../../common/components/AddInstitutionSheet';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import InstitutionAccounts from '../../holistic/components/InstitutionAccounts';
import useConnectQuilttInstitution from '../../holistic/hooks/useConnectQuilttInstitution';
import useGetHolisticSummary from '../../holistic/hooks/useGetHolisticSummary';
import { holisticAccountsAtom } from '../../holistic/state/HolisticAtoms';
import { captureException, trackForMembership } from '../../logger/Logger';
import { usePreMoneyMapCompleteness } from '../../onboarding/hooks/usePreMoneyMapCompleteness';
import { useUpdateMemberJourneyLog } from '../hooks/useUpdateMemberJourneyLog';

export const ExternalAccounts = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce(`Settings/ExternalAccounts`);

    const [addInstitutionSheetOpen, setAddInstitutionSheetOpen] =
        useState(false);

    // Call holistic summary to refresh values for the atom. Retain the loading state for when the query is invalidated, which happens in various onSuccess handlers for manual and quiltt accounts.
    const { isLoading: isLoadingHolisticData } = useGetHolisticSummary();
    const holisticAccounts = useAtomValue(holisticAccountsAtom);

    const { connectInstitution } = useConnectQuilttInstitution('profile');

    const { updateMemberJourneyLog } = useUpdateMemberJourneyLog();
    const { hasViewedLinkMoreAccounts, isFinancialProfileComplete } =
        usePreMoneyMapCompleteness();

    // Update member journey log after component renders
    useEffect(() => {
        const updateJourneyLog = async () => {
            try {
                await updateMemberJourneyLog({
                    viewedLinkMoreAccounts: true,
                });

                if (isFinancialProfileComplete && !hasViewedLinkMoreAccounts) {
                    trackForMembership(
                        AnalyticsMembershipEvent.PRE_MONEY_MAP_ONBOARDING_COMPLETED,
                    );
                }
            } catch (error) {
                captureException(error);
            }
        };

        void updateJourneyLog();
    }, [
        updateMemberJourneyLog,
        isFinancialProfileComplete,
        hasViewedLinkMoreAccounts,
    ]);

    // The External Accounts page only handles Active (or in process of Connecting) Quiltt and Manual accounts.
    const accountsToDisplay = holisticAccounts.filter((account) => {
        const isActiveQuilttAccount =
            account.provider === Provider.QUILTT &&
            (account.linkState === LinkState.CONNECTED ||
                account.linkState === LinkState.CONNECTING);
        const isManual = account.provider === Provider.MANUAL;

        return isActiveQuilttAccount || isManual;
    });
    // Group the accounts by institution.
    const accountsGroupedByInstitution = groupBy(
        accountsToDisplay,
        'institutionName',
    );

    if (isLoadingHolisticData) {
        return <FullScreenSpinner />;
    }

    return (
        <ScreenContainer title="External accounts" hasBackButton={true}>
            <YStack gap={24} justifyContent="space-between">
                <XStack
                    backgroundColor="$green400"
                    borderRadius={16}
                    padding={16}
                    alignItems="center"
                    gap={8}
                >
                    <View width={16}>
                        <Icon icon={InfoIcon} color="$green1100" size={16} />
                    </View>
                    <BodySmallRegular color="$green1100" flex={1}>
                        Link any savings or investment accounts to build a full
                        financial picture to share with your Guide.
                    </BodySmallRegular>
                </XStack>

                <YStack gap={24}>
                    {/* Institution / Accounts List */}
                    {Object.entries(accountsGroupedByInstitution).map(
                        ([institutionName, accounts], index) => {
                            // Use the first account in a set of accounts for an institution to get institution details.
                            const { logo, provider } = accounts[0] ?? {};

                            return (
                                <InstitutionAccounts
                                    key={index}
                                    institutionName={institutionName}
                                    institutionLogo={logo ?? ''}
                                    connectionId={
                                        accounts[0]?.connectionId ?? ''
                                    }
                                    accounts={accounts}
                                    isManual={provider === Provider.MANUAL}
                                    context="profile"
                                    shouldRenderAddAccountButton={false}
                                />
                            );
                        },
                    )}
                </YStack>

                {/* Add Account Button */}
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    text="Add an account"
                    icon={PlusIcon}
                    iconPosition="left"
                    onPress={() => {
                        setAddInstitutionSheetOpen(true);
                    }}
                />
            </YStack>

            <AddInstitutionSheet
                isOpen={addInstitutionSheetOpen}
                onClose={() => {
                    setAddInstitutionSheetOpen(false);
                }}
                launchInstitutionConnector={() => {
                    setAddInstitutionSheetOpen(false);
                    connectInstitution();
                }}
            />
        </ScreenContainer>
    );
};
